import { useMemo, useRef, useState } from "react";
import { BookOpen, PackageCheck, Search, Send, X } from "lucide-react";
import { STOCK, catalogUrl, packPay, campCount, logo, ron } from "@/lib/bf";
import BFSheet, { sheetRows } from "./BFSheet";
import BFReorder from "./BFReorder";
import BFCatalogBanner, { useBrandCatalogs } from "./BFCatalogBanner";
import { BFGamaBack, BFGamaTiles, gamasFor, inGama } from "./BFGamas";
const fold = (t) => t.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
const SYN = [["sampon", "shampoo"], ["masca", "mask", "maschera"], ["balsam", "conditioner"], ["ser", "serum", "siero"], ["ulei", "oil", "olio"],
  ["crema", "cream"], ["spuma", "mousse", "foam"], ["fixativ", "spray"], ["lotiune", "lotion"], ["vopsea", "color", "colore"], ["decolorant", "bleach", "powder"]];
const alts = (w) => [w, ...(SYN.find((g) => g.includes(w)) || [])];
const matcher = (q) => {
  const words = fold(q).split(/\s+/).filter(Boolean).map(alts);
  const compact = fold(q).replace(/[^a-z0-9]/g, "");
  return (hay) => { const h = fold(hay); return (compact && h.replace(/[^a-z0-9]/g, "").includes(compact)) || words.every((a) => a.some((x) => h.includes(x))); };
};

const OfferHero = ({ c, name }) => {
  const total = (c.min_list || 0) + (c.surprise_value || 0);
  const pay = Math.round(total * (100 - c.discount)) / 100;
  return (
    <div className="bf-offer-hero" data-testid="bf-offer-hero">
      <p className="bf-offer-kicker">Oferta Black Friday {name}</p>
      <h2 data-testid="bf-offer-total">{c.surprise_value > 0 ? `Comandă de ${ron(total)}` : `Comandă de minim ${ron(c.min_list)}`}</h2>
      {c.surprise_value > 0 && <div className="bf-offer-eq">
        <div><b>{ron(c.min_list)}</b><small>produse alese de tine</small></div>
        <i>+</i>
        <div className="is-gift"><b>{ron(c.surprise_value)}</b><small><PackageCheck size={12} /> produse utile alese de noi</small></div>
      </div>}
      <div className="bf-offer-deal">
        <span className="bf-offer-disc" data-testid="bf-offer-discount">-{c.discount}%</span>
        <p>Plătești doar <b data-testid="bf-offer-pay">{ron(pay)}</b> pentru toată comanda de {ron(total)} la preț de listă, TVA inclus.{c.surprise_value > 0 ? ` Din cei ${ron(total)}, tu alegi produse de ${ron(c.min_list)}, iar de restul de ${ron(c.surprise_value)} punem noi produse utile pentru salonul tău.` : " Discountul se aplică la toată comanda."}</p>
      </div>
      <p className="bf-offer-note">Suma se atinge doar cu produse {name}. Brandurile nu se pot combina.</p>
      <p className="bf-stock" data-testid="bf-offer-stock">{STOCK}</p>
    </div>
  );
};

const Progress = ({ c, n }) => {
  const pct = Math.min(100, (n.list / c.min_list) * 100);
  const ok = n.list >= c.min_list;
  const pk = ok ? packPay(c) : 0;
  return (
    <div className={`bf-offer-progress ${ok ? "is-ok" : ""}`} data-testid="bf-offer-progress">
      <div className="bf-offer-bar"><i style={{ width: `${pct}%` }} /></div>
      <p data-testid="bf-offer-status">{ok ? (c.surprise_value > 0 ? `Prag atins ✓ Adăugăm produsele de ${ron(c.surprise_value)} alese de noi (${ron(pk)} după reducere)` : `Prag atins ✓ Ai -${c.discount}% la toată comanda`) : `Ai ales ${ron(n.list)} din ${ron(c.min_list)} · mai ai ${ron(c.min_list - n.list)}`}</p>
      <b data-testid="bf-offer-sum">De plată: {ron(n.sum + pk)}</b>
      <button type="button" className="bf-sheet-send" disabled={!ok} onClick={() => window.dispatchEvent(new Event("bf-open-cart"))} data-testid="bf-sheet-send">
        <Send size={15} /> {ok ? "Trimite comanda ta" : `Trimite comanda ta (mai ai ${ron(c.min_list - n.list)})`}
      </button>
    </div>
  );
};

export const OfferView = ({ cfg, c, back, cart, setQty }) => {
  const byId = Object.fromEntries(cfg.products.map((p) => [p.id, p]));
  const name = cfg.brands.find((x) => x.id === c.brand)?.name || "";
  const [q, setQ] = useState(() => { const v = sessionStorage.getItem("bf_q") || ""; sessionStorage.removeItem("bf_q"); return v; });
  const [g, setG] = useState(null);
  const gamas = useMemo(() => gamasFor(cfg, c.brand, c.items), [cfg, c]);
  const listRef = useRef(null);
  const toList = () => requestAnimationFrame(() => {
    const el = listRef.current;
    if (!el) return;
    const st = document.querySelector(".bf-offer-sticky");
    const pad = st && getComputedStyle(st).position === "sticky" ? st.offsetHeight : 0;
    window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - pad - 12, behavior: "smooth" });
  });
  const useTiles = gamas.length > 1 && !q.trim();
  const all = useMemo(() => sheetRows(c, byId), [c, cfg.products]); // eslint-disable-line react-hooks/exhaustive-deps
  const hit = matcher(q);
  const rows = q.trim() ? all.filter((r) => hit(r.hay)) : g ? all.filter((r) => inGama(byId[r.key.split(":")[2]], g)) : all;
  const catalogs = useBrandCatalogs(c.brand);
  const n = campCount(c, cart, byId);
  const elsewhere = q && !rows.length ? cfg.campaigns.filter((o) => o.category === "oferta" && o.brand !== c.brand).map((o) => ({
    brand: o.brand, name: cfg.brands.find((x) => x.id === o.brand)?.name || o.brand,
    n: o.items.filter((id) => hit(`${byId[id]?.code || ""} ${byId[id]?.name} ${(byId[id]?.shades || []).map((x) => x.code).join(" ")}`)).length,
  })).filter((o) => o.n > 0) : [];
  return (
    <section className="bf-zone bf-zone--cat bf-offer" data-testid={`bf-offer-${c.brand}`}>
      {back}
      <img className="bf-zone-logo" src={logo(c.brand)} alt={name} />
      <BFCatalogBanner catalogs={catalogs} />
      <OfferHero c={c} name={name} />
      {!c.items.length ? <p className="bf-zone-sub" data-testid="bf-offer-empty">Produsele {name} se adaugă în curând.</p> : (
        <>
          <div className="bf-offer-sticky">
            <Progress c={c} n={n} />
            <div className="bf-offer-find" data-testid="bf-offer-find">
              <span className="bf-offer-find-label"><Search size={14} /> Filtrează tabelul după cod sau denumire</span>
              <label className="bf-offer-search">
                <Search size={20} />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Scrie codul sau numele produsului ca să filtrezi" data-testid="bf-offer-search" />
                {q && <button type="button" onClick={() => setQ("")} aria-label="Șterge căutarea" data-testid="bf-offer-search-clear"><X size={16} /></button>}
              </label>
              {catalogs.length > 0 && <div className="bf-sticky-cats">{catalogs.map((k) => <a key={k.id} className="bf-sheet-catlink bf-sticky-catlink" href={catalogUrl(k.id)} target="_blank" rel="noopener noreferrer" data-testid={`bf-sticky-catalog-${k.id}`}><BookOpen size={13} /> {k.title}</a>)}</div>}
            </div>
          </div>
          <div className="bf-camp bf-offer-list" ref={listRef}>
            <BFReorder c={c} cart={cart} setQty={setQty} />
            {useTiles && !g ? <BFGamaTiles gamas={gamas} onPick={(x) => { setG(x); toList(); }} /> : (
              <>
                {useTiles && g && <BFGamaBack g={g} onBack={() => { setG(null); toList(); }} />}
                <p className="bf-camp-hint bf-offer-start" data-testid="bf-offer-start">Scrie cantitatea dorită în dreapta fiecărui produs. Suma se actualizează pe loc, sus.</p>
                <BFSheet rows={rows} cart={cart} setQty={setQty} q={q} catalogs={catalogs} />
                {useTiles && g && <BFGamaBack g={g} onBack={() => { setG(null); toList(); }} />}
              </>
            )}
            {q.trim() && !rows.length && <p className="bf-camp-hint" data-testid="bf-offer-none">Niciun produs {name} găsit pentru „{q}”.</p>}
            {q.trim() && !rows.length && elsewhere.map((o) => (
              <button key={o.brand} type="button" className="bf-offer-elsewhere" onClick={() => { sessionStorage.setItem("bf_q", q); window.location.hash = `b/${o.brand}`; }} data-testid={`bf-offer-elsewhere-${o.brand}`}>
                Am găsit „{q}” la <b>{o.name}</b> ({o.n} {o.n === 1 ? "produs" : "produse"}) · deschide oferta {o.name} →
              </button>
            ))}
          </div>
        </>
      )}
    </section>
  );
};
